'use client';

import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { ButtonPreset } from '@/constants/componentPresets';
import { Temper } from '@/types/frontend-types';
import type { DataElScope } from '@/utils/dataEl';
import { MessageModalContentArea } from './MessageModalContentArea';
import { MessageModalFooter } from './MessageModalFooter';
import { MessageModalFooterNote } from './MessageModalFooterNote';
import { MessageModalHeader } from './MessageModalHeader';
import { MessageModalSurface } from './MessageModalSurface';
import { MessageModalTriangle } from './MessageModalTriangle';

interface MessageModalPanelProps {
  dataElScope: DataElScope;
  loading: boolean;
  primaryLoading: boolean;
  reduceMotion: boolean;
  temper: Temper;
  title: string;
  titleNode?: ReactNode;
  hackerAllCaps: boolean;
  body?: string;
  children?: ReactNode;
  footerNote?: string;
  primaryButtonText?: string;
  primaryButtonPreset: ButtonPreset;
  secondaryButtonText?: string;
  isMobile: boolean;
  isGlobalLayout: boolean;
  footerButtonStyle: CSSProperties;
  messageModalFooterStyle: CSSProperties;
  messageModalFooterButtonPadding?: string;
  messageModalFooterButtonHeight?: number;
  messageModalSurfaceBoxShadow: string;
  messageModalSurfaceStyle: CSSProperties;
  messageModalContentAreaStyle?: CSSProperties;
  messageModalContentAreaHeight?: string;
  messageModalSurfaceHeight?: string;
  messageModalSurfacePadding?: string;
  messageModalSurfaceJustifyContent?: 'center';
  onPrimaryClick: () => void;
  onSecondaryClick: () => void;
}

export function MessageModalPanel({
  dataElScope,
  loading,
  primaryLoading,
  reduceMotion,
  temper,
  title,
  titleNode,
  hackerAllCaps,
  body,
  children,
  footerNote,
  primaryButtonText,
  primaryButtonPreset,
  secondaryButtonText,
  isMobile,
  isGlobalLayout,
  footerButtonStyle,
  messageModalFooterStyle,
  messageModalFooterButtonPadding,
  messageModalFooterButtonHeight,
  messageModalSurfaceBoxShadow,
  messageModalSurfaceStyle,
  messageModalContentAreaStyle,
  messageModalContentAreaHeight,
  messageModalSurfaceHeight,
  messageModalSurfacePadding,
  messageModalSurfaceJustifyContent,
  onPrimaryClick,
  onSecondaryClick,
}: MessageModalPanelProps): ReactElement {
  return (
    <MessageModalSurface
      dataElScope={dataElScope}
      loading={loading}
      temper={temper}
      reduceMotion={reduceMotion}
      boxShadow={messageModalSurfaceBoxShadow}
      surfaceStyle={messageModalSurfaceStyle}
      height={messageModalSurfaceHeight}
      padding={messageModalSurfacePadding}
      justifyContent={messageModalSurfaceJustifyContent}
    >
      <MessageModalTriangle dataElScope={dataElScope} temper={temper} isGlobalLayout={isGlobalLayout} />

      <MessageModalHeader
        dataElScope={dataElScope}
        title={title}
        titleNode={titleNode}
        hackerAllCaps={hackerAllCaps}
        temper={temper}
      />

      <MessageModalContentArea
        dataElScope={dataElScope}
        body={body}
        contentAreaHeight={messageModalContentAreaHeight}
        contentAreaStyle={messageModalContentAreaStyle}
      >
        {children}
      </MessageModalContentArea>

      <MessageModalFooter
        dataElScope={dataElScope}
        loading={loading}
        primaryLoading={primaryLoading}
        primaryButtonText={primaryButtonText}
        primaryButtonPreset={primaryButtonPreset}
        secondaryButtonText={secondaryButtonText}
        footerStyle={messageModalFooterStyle}
        footerButtonStyle={footerButtonStyle}
        footerButtonPadding={messageModalFooterButtonPadding}
        footerButtonHeight={messageModalFooterButtonHeight}
        onPrimaryClick={onPrimaryClick}
        onSecondaryClick={onSecondaryClick}
      />

      <MessageModalFooterNote footerNote={footerNote} />
    </MessageModalSurface>
  );
}
